import React from "react";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import { AnalyticsFilters } from "../types/analytics.types";
import { useCostsAnalytics } from "../hooks/useAnalytics";
import { CostGroup, CostUser } from "../types/growth.types";
import {
  StatCard,
  ChartCard,
  DataTable,
  Column,
  LoadingState,
  ErrorState,
  EmptyState,
} from "../design/ui";
import {
  lineOptions,
  barOptions,
  doughnutOptions,
  lineDataset,
  barDataset,
  doughnutDataset,
  formatCompact,
  formatCurrency,
} from "../design/chartTheme";

interface Props {
  filters: AnalyticsFilters;
}

const shortEndpoint = (endpoint?: string) => (endpoint || "unknown").replace(/^\/api\/v1\//, "");

export const CostsTab: React.FC<Props> = ({ filters }) => {
  const { data, loading, error, refetch } = useCostsAnalytics(filters);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error.message} onRetry={refetch} />;
  if (!data) return <EmptyState />;

  const { totals, daily, by_model, by_endpoint, top_users } = data;

  const dayLabels = daily.map((d) => d.date.slice(5));
  const costChart = {
    labels: dayLabels,
    datasets: [
      lineDataset(
        "Daily cost ($)",
        daily.map((d) => d.cost),
        4,
        true
      ),
    ],
  };

  const modelCostChart = {
    labels: by_model.map((m) => m.model || "unknown"),
    datasets: [doughnutDataset(by_model.map((m) => m.cost))],
  };

  const topEndpoints = by_endpoint.slice(0, 8);
  const endpointCostChart = {
    labels: topEndpoints.map((e) => shortEndpoint(e.endpoint)),
    datasets: [
      barDataset(
        "Cost ($)",
        topEndpoints.map((e) => e.cost),
        1
      ),
    ],
  };

  const endpointColumns: Column<CostGroup>[] = [
    {
      key: "endpoint",
      header: "Feature / Endpoint",
      render: (r) => shortEndpoint(r.endpoint),
    },
    {
      key: "requests",
      header: "Requests",
      align: "right",
      render: (r) => r.requests.toLocaleString(),
    },
    { key: "tokens", header: "Tokens", align: "right", render: (r) => formatCompact(r.tokens) },
    {
      key: "unique_users",
      header: "Users",
      align: "right",
      render: (r) => r.unique_users.toLocaleString(),
    },
    { key: "cost", header: "Cost", align: "right", render: (r) => formatCurrency(r.cost, 4) },
    {
      key: "avg_cost_per_request",
      header: "Avg/req",
      align: "right",
      render: (r) => formatCurrency(r.avg_cost_per_request, 4),
    },
  ];

  const userColumns: Column<CostUser>[] = [
    { key: "user_id", header: "User", render: (r) => r.user_id },
    {
      key: "requests",
      header: "Requests",
      align: "right",
      render: (r) => r.requests.toLocaleString(),
    },
    { key: "tokens", header: "Tokens", align: "right", render: (r) => formatCompact(r.tokens) },
    { key: "cost", header: "Cost", align: "right", render: (r) => formatCurrency(r.cost, 4) },
  ];

  return (
    <div>
      <div className="an-stat-grid">
        <StatCard
          label="Total Cost"
          value={formatCurrency(totals.total_cost)}
          sub="In selected range"
        />
        <StatCard
          label="Total Tokens"
          value={formatCompact(totals.total_tokens)}
          sub={`${formatCompact(totals.input_tokens)} in / ${formatCompact(totals.output_tokens)} out`}
        />
        <StatCard
          label="Requests"
          value={totals.total_requests.toLocaleString()}
          sub={`${totals.unique_users.toLocaleString()} users`}
        />
        <StatCard
          label="Avg Cost / Request"
          value={formatCurrency(totals.avg_cost_per_request, 4)}
        />
        <StatCard
          label="Avg Tokens / Request"
          value={Math.round(totals.avg_tokens_per_request).toLocaleString()}
        />
        <StatCard
          label="Cost / Active User"
          value={formatCurrency(
            totals.unique_users ? totals.total_cost / totals.unique_users : 0,
            3
          )}
          sub="LLM spend per user"
        />
      </div>

      <ChartCard
        title="Daily LLM Cost"
        subtitle="Total spend per day"
        height={300}
        className="an-chart-grid--full"
      >
        {daily.length ? (
          <Line data={costChart} options={lineOptions()} />
        ) : (
          <EmptyState title="No LLM usage in range" message="" />
        )}
      </ChartCard>

      <div className="an-chart-grid">
        <ChartCard title="Cost by Model" height={300}>
          {by_model.length ? (
            <Doughnut data={modelCostChart} options={doughnutOptions()} />
          ) : (
            <EmptyState title="No data" message="" />
          )}
        </ChartCard>
        <ChartCard title="Cost by Feature" subtitle="Top endpoints" height={300}>
          {topEndpoints.length ? (
            <Bar
              data={endpointCostChart}
              options={barOptions({
                indexAxis: "y",
                plugins: { legend: { display: false } },
              })}
            />
          ) : (
            <EmptyState title="No data" message="" />
          )}
        </ChartCard>
      </div>

      <ChartCard title="Spend by Feature" subtitle="Every endpoint, ranked by cost">
        {by_endpoint.length ? (
          <DataTable
            columns={endpointColumns}
            rows={by_endpoint}
            rowKey={(r) => r.endpoint || "unknown"}
            initialSortKey="cost"
            pageSize={12}
          />
        ) : (
          <EmptyState title="No data" message="" />
        )}
      </ChartCard>

      <ChartCard title="Top Users by Spend" subtitle="Highest LLM cost">
        {top_users.length ? (
          <DataTable
            columns={userColumns}
            rows={top_users}
            rowKey={(r) => r.user_id}
            initialSortKey="cost"
            pageSize={10}
          />
        ) : (
          <EmptyState title="No data" message="" />
        )}
      </ChartCard>
    </div>
  );
};

export default CostsTab;
